import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Users, History, ShoppingCart, BarChart3, AlertTriangle } from 'lucide-react';
import PurchaseRulesConfiguration from '../GoDaddy/PurchaseRulesConfiguration';
import ContactProfileManager from '../GoDaddy/ContactProfileManager';
import PurchaseHistory from '../GoDaddy/PurchaseHistory';
import DomainPurchaseWizard from '../GoDaddy/DomainPurchaseWizard';
import SpendingAnalyticsDashboard from '../GoDaddy/SpendingAnalyticsDashboard';
import AnomalyDetectionConfig from '../GoDaddy/AnomalyDetectionConfig';
import AnomalyHistoryPanel from '../GoDaddy/AnomalyHistoryPanel';

const GoDaddySettingsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'rules' | 'contacts' | 'history' | 'analytics' | 'anomalies'>('rules');
  const [showPurchaseWizard, setShowPurchaseWizard] = useState(false);

  // Convert projectId to number
  const numericOperationId = projectId ? parseInt(projectId, 10) : 0;

  // Purchase wizard handlers
  const handleOpenPurchaseWizard = () => {
    setShowPurchaseWizard(true);
  };

  const handlePurchaseComplete = useCallback(() => {
    setShowPurchaseWizard(false);
    setActiveTab('history');
  }, []);

  const handlePurchaseCancel = useCallback(() => {
    setShowPurchaseWizard(false);
  }, []);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showPurchaseWizard) {
        handlePurchaseCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPurchaseWizard, handlePurchaseCancel]);

  // Callback handlers
  const handleSaveRules = () => {
    // Rules saved successfully - could show a toast notification
    console.log('Purchase rules saved successfully');
  };

  const handleProfileSelect = (profile: any) => {
    // Profile selected - could show details or update UI
    console.log('Contact profile selected:', profile);
  };

  const handleViewDetails = (purchaseId: number) => {
    // View purchase details - could navigate or show modal
    console.log('View purchase details:', purchaseId);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-cap-dark border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(`/projects/${projectId}`)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-warm-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Project
          </button>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">
                GoDaddy Domain Purchase Settings
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Configure autonomous domain purchasing, contact profiles, and view purchase history
              </p>
            </div>
            <button
              onClick={handleOpenPurchaseWizard}
              data-testid="purchase-new-domain-button"
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Purchase New Domain
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-cap-dark border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8" role="tablist">
            <button
              onClick={() => setActiveTab('rules')}
              role="tab"
              aria-selected={activeTab === 'rules'}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'rules'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Purchase Rules
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              role="tab"
              aria-selected={activeTab === 'contacts'}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'contacts'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Contact Profiles
            </button>
            <button
              onClick={() => setActiveTab('history')}
              role="tab"
              aria-selected={activeTab === 'history'}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              <History className="w-4 h-4 inline mr-2" />
              Purchase History
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              role="tab"
              aria-selected={activeTab === 'analytics'}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Analytics
            </button>
            <button
              onClick={() => setActiveTab('anomalies')}
              role="tab"
              aria-selected={activeTab === 'anomalies'}
              data-testid="anomalies-tab"
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'anomalies'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              Anomaly Detection
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'rules' && (
          <PurchaseRulesConfiguration
            projectId={numericOperationId}
            onSave={handleSaveRules}
          />
        )}
        {activeTab === 'contacts' && (
          <ContactProfileManager
            projectId={numericOperationId}
            onProfileSelect={handleProfileSelect}
          />
        )}
        {activeTab === 'history' && (
          <PurchaseHistory
            projectId={numericOperationId}
            onViewDetails={handleViewDetails}
          />
        )}
        {activeTab === 'analytics' && (
          <SpendingAnalyticsDashboard projectId={numericOperationId} />
        )}
        {activeTab === 'anomalies' && (
          <div className="space-y-8">
            <AnomalyDetectionConfig projectId={numericOperationId} />
            <AnomalyHistoryPanel projectId={numericOperationId} />
          </div>
        )}
      </div>

      {/* Purchase Wizard Modal */}
      {showPurchaseWizard && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          data-testid="purchase-wizard-modal"
        >
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <DomainPurchaseWizard
              projectId={numericOperationId}
              onComplete={handlePurchaseComplete}
              onCancel={handlePurchaseCancel}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GoDaddySettingsPage;
